import {Box, Divider} from '@mui/material';
import DocExplorerHeader from './doc-explorer-header/DocExplorerHeader';
import ExpandableTreeView from './doc-expandable-tree/ExpandableTreeView';
import './ViewDocExplorer.scss';
import {
  useGetDocExplorerColumnConfigQuery,
  useResetDocColumnConfig,
  useSaveDocColumnConfig,
  useTreeNodesList,
  useGetDocMetadataFilterQuery,
  useCreateMasterDocList,
} from '../../hooks/document/useDocument';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
  DataGridRowSelectionModel,
  DocActionMenuItem,
  DocColumnUpdatePayload,
  DocExplorerColumnConfig,
  DocExplorerColumnHeader,
  ExtendedTreeItemProps,
} from '../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import DocExplorerFilterAccordion, {
  ActiveFilter,
  filterSelectionType,
} from './doc-filter/doc-filter-accordion/DocExplorerFilterAccordion';

import {t, TFunction} from 'i18next';
import {useQueryClient} from '@tanstack/react-query';
import {getTreeNodesById} from '../../api/endpoints/documentApi';
import {
  DocExplorerMasterRequest,
  ITreeNodeResponse,
} from '../../api/types/document.type';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  BgNavigationTypes,
  RoleId,
  SourceTypeCode,
  ToastType,
} from '../../shared/constants';
import DocExplorerDataGrid from './doc-explorer-data-grid/DocExplorerDataGrid';
import {GridColumnOrderChangeParams} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import BulkFolderUnFolder from '../action-menu/bulk-folder-unfolder/BulkFolderUnFolder';
import ActionLikeDocuments, {
  IActionListItem,
} from '../action-menu/action-like-documents/ActionLikeDocuments';
import BulkAssignFlyout from '../action-menu/bulk-assign-flyout/BulkAssignFlyout';
import QuickBatchFlyout from '../action-menu/quick-batch-flyout/QuickBatchFlyout';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import BulkTagUntag from '../action-menu/bulk-tag-untag/BulkTagUntag';
import BulkTaggingWarning from '../action-menu/bulk-tagging-warning/BulkTaggingWarning';
import BulkReleaseFlyout from '../action-menu/bulk-release-flyout/BulkReleaseFlyout';
import TableHeader, {SelectSubItem} from './doc-list/table-header/TableHeader';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../redux/slice/docListSlice';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../redux/slice/docViewSlice';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import {clearDocViewState} from '../document-viewer/docViewerUtils';
import {deepClone} from '@mui/x-data-grid/internals';
import {
  useCallBulkActionsLikePopup,
  useCallBulkActionsPopup,
  useCallBulkActionsPopupNonBg,
  useDoAction,
  useDoActionNonBg,
  useGetBulkAssignDocListId,
} from '../../hooks/assignments/useAssignments';

import {
  IBulkActionLikePopupRequest,
  IBulkActionLikePopupResponse,
  IBulkActionPopupNonBgResponse,
  IBulkActionPopupResponse,
  IBulkAssignDocListIdRequest,
  IBulkAssignDocListIdResponse,
} from '../../api/types/assignment.type';
import {isEmpty} from 'lodash';
import {callBulkActionsLikePopupNonbg} from '../../api/endpoints/assignmentsApi';
import ArrDocMaxLimitWarning from '../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  setBulkAssignFlowData,
  setSelectedData,
} from '../../redux/slice/assignmentSlice';

export const DOC_EXPLORER_ACTION_MENU: DocActionMenuItem[] = [
  {
    id: 'View',
    name: 'View',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
    subItems: [
      {
        id: 'ViewInDocView',
        name: 'ViewInDocView',
        Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
      },
      {
        id: 'ViewInDocViewInNewTab',
        name: 'ViewInDocViewInNewTab',
        Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
      },
      {
        id: 'ViewInDocList',
        name: 'ViewInDocList',
        Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
      },
    ],
  },
  {
    id: 'BulkTag',
    name: 'BulkTag',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
  {
    id: 'BulkFolder',
    name: 'BulkFolder',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
  {
    id: 'BulkRelease',
    name: 'BulkRelease',
    Roles: [RoleId.ProjectAdministrator],
  },
  {
    id: 'BulkAssign',
    name: 'BulkAssign',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'QuickBatch',
    name: 'QuickBatch',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'ExportData',
    name: 'ExportData',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
];

// -----Start: Need to check after new changes done for Action Like Menu Component -----
export const getInitialActionList = (
  t: TFunction<['translation', ...string[]], undefined>
): IActionListItem[] => {
  return [
    {
      id: 'family_members',
      label: t('common.actionMenu.familyMembers'),
      isLoading: false,
      count: 0,
      selected: false,
    },
    {
      id: 'email_docs',
      label: t('common.actionMenu.emailThread'),
      isLoading: false,
      count: 0,
      selected: false,
    },
    {
      id: 'near_duplicates',
      label: t('common.actionMenu.nearDuplicates'),
      isLoading: false,
      count: 0,
      selected: false,
    },
  ];
};

export type ActionSelection = {
  type: 'new' | 'existing';
  ids: string | string[];
};

const COLUMN_REORDER_UPDATE_DELAY = 2000; // milliseconds

export const ViewDocExplorer = () => {
  const dispatch = useDispatch();
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(['-1']);
  const [enableActionButton, setEnableActionButton] = useState<boolean>(false);
  const [docsData, setDocData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);

  const {data: treeNodes} = useTreeNodesList(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [unselectedIds, setUnselectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [isBulkFolderAction, setIsBulkFolderAction] = useState<boolean>(false);
  const [isBulkTagAction, setIsBulkTagAction] = useState<boolean>(false);
  const [isActionLikeModal, setIsActionLikeModal] = useState<boolean | string>(
    false
  );
  const [isBulkWarning, setIsBulkWarning] = useState<boolean>(false);
  const [isBulkReleaseAction, setIsBulkReleaseAction] =
    useState<boolean>(false);
  const [isBulkAssignAction, setIsBulkAssignAction] = useState<boolean>(false);
  const [isQuickBatchAction, setIsQuickBatchAction] = useState<boolean>(false);

  const [columnMenu, setColumnMenu] = useState<SelectSubItem[]>([]);
  const [docColumnConfig, setDocColumnConfig] = useState<
    DocExplorerColumnConfig | undefined
  >();
  const [totalGridCount, setTotalGridCount] = useState<number>(0);
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<
    {columnName: string; filterValue: string}[]
  >([]);
  const [actionMenuColumnFilters, setActionMenuColumnFilters] = useState<
    {ColumnName: string; FilterValue: string}[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const {data: metadataFilters} = useGetDocMetadataFilterQuery();
  const [dataGridResetKey, setDataGridResetKey] = useState<number>(0);
  const [masterDocListId, setMasterDocListId] = useState<number>(0);
  const [analyticsIndicatorStatus, setAnalyticsIndicatorStatus] =
    useState<number>(0);
  const [bulkAssignmentId, setBulkAssignmentId] = useState<number>(0);
  const [, setNonBgStandardCount] = useState<number>(0);
  const [selectedNewExistingTag, setSelectedNewExistingTag] = useState<
    ActionSelection | null | undefined
  >();
  const [newTagId, setNewTagId] = useState<string | null | undefined>();
  const [selectedNewExistingFolder, setSelectedNewExistingFolder] = useState<
    ActionSelection | null | undefined
  >();
  const [newFolderId, setNewFolderId] = useState<string | null | undefined>();
  const [isNonbg] = useState<boolean>(false);
  const [bulkSelectedAction, setBulkSelectedAction] = useState<string>('');

  const [selectedTreeIds, setSelectedTreeIds] = useState<string[]>([]);

  const [actionList] = useState(() => getInitialActionList(t));
  const [quickBatchAssignmentId, setQuickBatchAssignmentId] = useState<
    number | undefined
  >(undefined);
  const [securityGroupId, setSecurityGroupId] = useState<string[]>([]);
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const docListMasterId = useSelector(
    (state: RootState) => state.docView.documentListMasterId
  );
  const pendingAssignAction = useRef<string>('');
  const assignmentSelectedTab = useRef<'existing' | 'new'>('existing');
  const persistContentForAssign = useRef<boolean>(false);
  const samplingMethodForAssign = useRef<string>('count');
  const countForAssign = useRef<number>(1);
  const percentForAssign = useRef<number>(1);
  const docNumberToUnassign = useRef<number>(0);

  const [actionLikeChangeList, setActionLikeChangeList] =
    useState<IActionListItem[]>();
  const {data: docExplorerColumnConfigData} =
    useGetDocExplorerColumnConfigQuery();

  const [showDocLimitWarning, setShowDocLimitWarning] =
    useState<boolean>(false);

  const [navigationType, setNavigationType] = useState<SourceTypeCode>();

  useEffect(() => {
    const udpatedColumnFilters =
      columnFilters.map((item) => ({
        ColumnName: item.columnName,
        FilterValue: item.filterValue,
      })) ?? [];
    setActionMenuColumnFilters(udpatedColumnFilters);
  }, [columnFilters]);

  const {mutate: saveColumnConfig} = useSaveDocColumnConfig(() => {
    setToastMessage(t('docExplorer.docDataGrid.columnSaveSuccessToastText'));
    setToastStatus(ToastType.Success);
    setShowToast(true);
  });
  const {mutate: resetColumnConfig} = useResetDocColumnConfig(() => {
    setDocColumnConfig((prev) => {
      if (!prev) return prev;
      return {...prev, userProfileColumnHeaders: prev?.defaultColumnHeaders};
    });
    setColumnMenu(
      getColumnMenuItems(
        docExplorerColumnConfigData?.defaultColumnHeaders ?? []
      )
    );
  });

  const {mutate: callBulkActionsPopup, isPending: isBulkActionsPopupLoading} =
    useCallBulkActionsPopup(
      (response: IBulkActionPopupResponse) => {
        const newMasterDocListId = response.masterDocListID;
        setMasterDocListId(newMasterDocListId);
      },
      (_error) => {
        setToastMessage(
          t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError')
        );
        setToastStatus(ToastType.Error);
        setShowToast(true);
      }
    );

  const {mutate: callBulkActionsPopupNonBg} = useCallBulkActionsPopupNonBg(
    (response: IBulkActionPopupNonBgResponse) => {
      const newNonBgStandardCount = response.documentCounts.standardCount;
      setNonBgStandardCount(newNonBgStandardCount);
    },
    (_error) => {
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const {mutate: doAction} = useDoAction(
    (response) => {
      let successToastMessage = t(
        'docExplorer.actionMenu.bulkTaggingWarning.doActionSuccess'
      );
      if (pendingAssignAction.current === 'assign_documents') {
        samplingMethodForAssign.current = 'count';
        countForAssign.current = 1;
        percentForAssign.current = 1;
        persistContentForAssign.current = false;
        successToastMessage = t('docExplorer.bulkAssignSuccessMessage');
      }

      if (pendingAssignAction.current === 'unassign_documents') {
        successToastMessage = t('docExplorer.bulkUnAssignSuccessMessage');
      }

      if (pendingAssignAction.current === 'quickBatch') {
        successToastMessage = t('docExplorer.quickBatchSuccessMessage');
      }

      setIsBulkWarning(false);
      setIsBulkTagAction(false);
      if (
        pendingAssignAction.current === 'assign_documents' &&
        assignmentSelectedTab.current == 'new'
      ) {
        // Store bulk assign flow data in Redux
        const parentGroupId =
          selectedTreeIds.length > 0 ? Number(selectedTreeIds[0]) : 0;
        const bulkActionId = response?.bulkActionId ?? 0;
        dispatch(
          setBulkAssignFlowData({
            parentGroupId,
            masterDocListId,
            assignmentId: bulkAssignmentId,
            bulkActionId,
          })
        );
        dispatch(
          setSelectedData({
            assignmentId: bulkAssignmentId,
            assignmentGroupId: parentGroupId,
            type: 'assignment',
            mode: 'create',
          })
        );
        navigate('/Assignment/Create');
      } else {
        setToastMessage(successToastMessage);
        setToastStatus(ToastType.Success);
        setShowToast(true);
      }
      pendingAssignAction.current = '';
    },
    (_error) => {
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.doActionError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const {mutate: doActionNonBg} = useDoActionNonBg(
    () => {
      setIsBulkWarning(false);
      setIsBulkTagAction(false);
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.doActionSuccess')
      );
      setToastStatus(ToastType.Success);
      setShowToast(true);
    },
    (_error) => {
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.doActionError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const {mutate: getBulkAssignDocListId} = useGetBulkAssignDocListId(
    (response: IBulkAssignDocListIdResponse) => {
      const newMasterDocListId = response.masterDocListID;
      const assignmentId = response.assignmentId;
      setMasterDocListId(newMasterDocListId);
      setBulkAssignmentId(assignmentId);

      if (pendingAssignAction.current === 'assign_documents') {
        callBulkActionsLikePopupApiCall(
          'ASSIGN',
          selectedTreeIds,
          pendingAssignAction.current,
          newMasterDocListId,
          () => {
            setIsActionLikeModal('bulkAssign');
          },
          persistContentForAssign.current
        );
      }

      if (pendingAssignAction.current === 'unassign_documents') {
        doActionApiCall(
          'ASSIGN',
          selectedTreeIds,
          'unassign_documents',
          undefined,
          docNumberToUnassign.current,
          newMasterDocListId
        );
      }

      if (pendingAssignAction.current === 'quickBatch') {
        callBulkActionsLikePopupApiCall(
          'QUICKASSIGNMENT',
          quickBatchAssignmentId ? [quickBatchAssignmentId.toString()] : [],
          pendingAssignAction.current,
          newMasterDocListId,
          () => {
            setIsActionLikeModal('quickBatch');
          }
        );
      }
    },
    (_error) => {
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const {mutate: callBulkActionsLikePopup} = useCallBulkActionsLikePopup(
    (response: IBulkActionLikePopupResponse) => {
      const newMasterDocListId = response.masterDocListID;
      setMasterDocListId(newMasterDocListId);
      setAnalyticsIndicatorStatus(response?.analyticsIndicatorStatus);
    },
    (_error) => {
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    const dataArray = Array.isArray(treeNodes) ? treeNodes : [treeNodes];

    const root = dataArray.flatMap((item) => {
      if (!item) return [];
      const rootNode = convertTreeNodeToTreeItem(item);
      rootNode.children = Array.isArray(item.children)
        ? item.children.map(convertTreeNodeToTreeItem)
        : [];
      return rootNode;
    });
    setDocData(root);
  }, [treeNodes]);

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const filteredActions = (() => {
    if (!loginUserData) return [];
    const userRole = String(
      loginUserData.roleId ?? loginUserData.baseRoleId ?? ''
    );
    const mapAndFilter = (items: DocActionMenuItem[]): SelectSubItem[] =>
      items
        .filter((it) => !it.Roles || it.Roles.includes(userRole))
        .map((it) => ({
          id: it.id,
          name: t(`docExplorer.actionMenu.items.${it.name}`),
          disabled: it.disabled,
          subItems: it.subItems ? mapAndFilter(it.subItems) : undefined,
        }));
    return mapAndFilter(DOC_EXPLORER_ACTION_MENU);
  })();

  const handleSelectedNode = (values: string[] | null) => {
    if (values) {
      setSelectedNodeIds(values);
      handleSelectionStateChange({
        isAllSelected: false,
        selectedIds: [],
        unselectedIds: [],
      });
    }
  };

  const convertTreeNodeToTreeItem = useCallback(
    (node: ITreeNodeResponse): TreeViewBaseItem<ExtendedTreeItemProps> => {
      const hasChildren =
        node.children === true ||
        (Array.isArray(node.children) && node.children.length > 0);

      return {
        id: node.id,
        label: node.text,
        disabled: node.state?.disabled ?? false,
        isChildrenLoaded: node.state?.selected ?? false,
        hasChildren: hasChildren,
        children: hasChildren
          ? [
              {
                id: `${node.id}-loading`,
                label: 'Loading...',
                disabled: true,
              },
            ]
          : undefined,
      };
    },
    []
  );

  const handleNodeExpand = async (
    nodeId: string
  ): Promise<TreeViewBaseItem<ExtendedTreeItemProps>[]> => {
    const response = await queryClient.fetchQuery<ITreeNodeResponse>({
      queryKey: ['useTreeNodesList', parseInt(nodeId)],
      queryFn: () => getTreeNodesById(parseInt(nodeId)),
    });

    const children =
      response?.jsDocNodeTreeDetailsResponse?.map(convertTreeNodeToTreeItem) ??
      [];
    return children;
  };

  const columnResetClickHandler = () => {
    setActiveFilters([]);
    setDataGridResetKey((prev) => prev + 1);
    resetColumnConfig(docColumnConfig?.defaultColumnHeaders ?? []);
  };

  const columnSaveClickHandler = () => {
    const payLoadData: DocColumnUpdatePayload[] =
      docColumnConfig?.userProfileColumnHeaders.map((item) => {
        return {
          columnName: item.docExplorerColCode,
          isSortable: item.shouldSortBy,
          isVisible: item.isVisible,
        } as DocColumnUpdatePayload;
      }) ?? [];

    saveColumnConfig(payLoadData);
  };

  const columnMenuSelectionChangeHandler = (column: SelectSubItem) => {
    setColumnMenu((prev) => {
      return prev.map((item) => {
        if (column.id == item.id) return {...item, checked: !item.checked};
        return {...item};
      });
    });

    setDocColumnConfig((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        userProfileColumnHeaders: prev.userProfileColumnHeaders.map((item) =>
          column.id === item.docExplorerColCode
            ? {...item, isVisible: !item.isVisible}
            : item
        ),
      };
    });
  };

  const columnOrderChangeHandler = (orderInfo: GridColumnOrderChangeParams) => {
    const {oldIndex, targetIndex} = orderInfo;
    const visibleColumns =
      docColumnConfig?.userProfileColumnHeaders.filter(
        (col) => col.isVisible
      ) ?? [];
    const hiddenColumns =
      docColumnConfig?.userProfileColumnHeaders.filter(
        (col) => !col.isVisible
      ) ?? [];
    const selectedColumn = visibleColumns[oldIndex - 1];
    const nonSelectedVisibleColumns = visibleColumns.filter(
      (col) => col.docExplorerColCode != selectedColumn.docExplorerColCode
    );

    if (oldIndex === targetIndex) return;
    const orderedVisibleColumns = [];

    for (let i = 0; i < nonSelectedVisibleColumns.length; i++) {
      if (oldIndex > targetIndex && i == targetIndex - 1) {
        orderedVisibleColumns.push(selectedColumn);
      }
      orderedVisibleColumns.push(nonSelectedVisibleColumns[i]);
      if (oldIndex < targetIndex && i == targetIndex - 1 - 1) {
        orderedVisibleColumns.push(selectedColumn);
      }
    }

    // Update state
    setTimeout(() => {
      setDocColumnConfig((prev) =>
        prev
          ? {
              ...prev,
              userProfileColumnHeaders: [
                ...orderedVisibleColumns,
                ...hiddenColumns,
              ],
            }
          : prev
      );

      setColumnMenu(
        getColumnMenuItems([...orderedVisibleColumns, ...hiddenColumns])
      );
    }, COLUMN_REORDER_UPDATE_DELAY);
  };

  const getColumnMenuItems = (
    columns: DocExplorerColumnHeader[]
  ): SelectSubItem[] => {
    return (
      columns.map((data) => {
        return {
          id: data.docExplorerColCode,
          name: data.docExplorerColTitle,
          checked: data.isVisible,
        };
      }) ?? []
    );
  };
  // ----------------

  const navigateToDocPage = () => {
    if (selectedActionId === 'ViewInDocView') {
      setShowDocLimitWarning(false);
      navigate('/DocumentViewer/DocView');
      return;
    }

    if (selectedActionId === 'ViewInDocViewInNewTab') {
      setShowDocLimitWarning(false);
      const newWindow = window.open('about:blank', '_blank');
      if (newWindow) {
        newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
      }
      return;
    }

    if (selectedActionId === 'ViewInDocList') {
      setShowDocLimitWarning(false);
      const backPath = `${window.location.pathname}${window.location.search}`;
      dispatch(
        setDocListBackNavigation({
          label: t('docExplorer.header.title'),
          path: backPath,
        })
      );
      navigate('/Document/DocList');
      return;
    }

    if (selectedActionId === 'ExportData') {
      setShowDocLimitWarning(false);
      navigate('/Report/ExportData', {
        state: {
          item: {
            navigationSourceType: SourceTypeCode.DOCEXPLORER,
            source: docListMasterId,
            sourceName: SourceTypeCode.DOCUMENT,
            sourceDisable: true,
          },
        },
      });
    }
  };
  // ----------------

  // Check the signalR message navigate accordingly
  useEffect(() => {
    if (!isNavigating) return;

    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        (navType === SourceTypeCode.DOCVIEW ||
          navType === SourceTypeCode.DOCLIST ||
          navType === SourceTypeCode.EXPORTDATA)
      ) {
        setIsLoading(false);
        setIsNavigating(false);
        navigateToDocPage();
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsLoading(false);
      setIsNavigating(false);
      setToastMessage(t('docExplorer.docDataGrid.navigationErrorToastText'));
      setToastStatus(ToastType.Error);
      setShowToast(true);
      return;
    }
  }, [lastEvent, selectedActionId, docListMasterId]);
  // ----------------

  const {mutate: createMasterList} = useCreateMasterDocList((response) => {
    clearDocViewState();

    dispatch(storeDocumentListMasterId(response.doclistId));
    dispatch(storeSourceTypeCode(SourceTypeCode.DOCUMENT));

    if (
      selectedActionId === 'ViewInDocList' ||
      selectedActionId === 'ViewInDocView'
    ) {
      dispatch(setDocListId(Number(response.doclistId)));
    }
  });

  useEffect(() => {
    setColumnMenu(
      getColumnMenuItems(
        docExplorerColumnConfigData?.userProfileColumnHeaders ?? []
      )
    );
    setDocColumnConfig(structuredClone(docExplorerColumnConfigData));
  }, [docExplorerColumnConfigData]);

  const handleSelectionStateChange = (arg0: DataGridRowSelectionModel) => {
    setSelectedIds(arg0.selectedIds);
    setUnselectedIds(arg0.unselectedIds);
    setIsAllSelected(arg0.isAllSelected);
    setEnableActionButton(arg0.isAllSelected || arg0.selectedIds.length > 0);
    setTotalGridCount(arg0.totalCount ?? 0);
  };

  const navigate = useNavigate();

  const prepareActiveFilterPayload = (filtersData: ActiveFilter[]) => {
    return filtersData.map((item) => {
      const {id, label, selectionType, values, dataType, endDate, startDate} =
        item;
      let operand: filterSelectionType;
      if (selectionType === 'include') {
        operand = 'IN';
      } else if (selectionType === 'exclude') {
        operand = 'NOTIN';
      } else {
        operand = selectionType || 'IN';
      }
      return {
        ProjectFieldId: id,
        ProjectFieldName: label,
        operand,
        values:
          dataType != 'DATETIME'
            ? values?.map((i) => i?.text)
            : [startDate, endDate],
        type: dataType === 'DATETIME' ? 1 : 2,
      };
    });
  };
  // ----------------

  const getDestinationTypeCode = (actionId: string) => {
    switch (actionId) {
      case 'ViewInDocView':
      case 'ViewInDocViewInNewTab':
        return SourceTypeCode.DOCVIEW;

      case 'ViewInDocList':
        return SourceTypeCode.DOCLIST;

      case 'ExportData':
        return SourceTypeCode.EXPORTDATA;

      default:
        return '';
    }
  };
  // ----------------

  const startDocumentNavigation = (
    actionId: string,
    totalDocumentToView: number
  ) => {
    const payload: DocExplorerMasterRequest = {
      docViewSessionObjects: {
        documentListMasterID: 0,
        sourceTypeCode: SourceTypeCode.DOCEXPLORER,
      },
      sourceInfo: {
        selectedNodeIDs: selectedNodeIds.join(','),
        filters: [],
        columnFilters,
        isInverse: isAllSelected,
        selectedDocIDs: isAllSelected ? '' : selectedIds.join(','),
        excludedDocIDs: isAllSelected ? unselectedIds.join(',') : '',
      },
      filterDetails: prepareActiveFilterPayload(deepClone(activeFilters)),
      destinationTypeCode: getDestinationTypeCode(actionId),
      documentCount: totalDocumentToView,
    };
    // Navigation actions
    setIsNavigating(true);
    setShowDocLimitWarning(false);
    setIsLoading(true);
    createMasterList(payload);
  };
  // ----------------

  const onActionSelect = (action: SelectSubItem | null) => {
    if (!action) return;

    const actionId = action.id?.toString();
    const totalDocumentCount = isAllSelected
      ? totalGridCount - unselectedIds.length
      : selectedIds.length;

    if (actionId === 'ViewInDocView' || actionId === 'ViewInDocViewInNewTab') {
      setNavigationType(SourceTypeCode.DOCVIEW);
    } else if (actionId === 'ViewInDocList') {
      setNavigationType(SourceTypeCode.DOCLIST);
    } else if (actionId === 'ExportData') {
      setNavigationType(SourceTypeCode.EXPORTDATA);
    }

    if (
      actionId === 'ViewInDocView' ||
      actionId === 'ViewInDocViewInNewTab' ||
      actionId === 'ViewInDocList'
    ) {
      setSelectedActionId(actionId);
      setShowDocLimitWarning(true);
    }

    if (actionId === 'ExportData') {
      setSelectedActionId(actionId);
      setNavigationType(SourceTypeCode.EXPORTDATA);
      startDocumentNavigation(actionId, totalDocumentCount);
    }

    if (
      actionId === 'BulkTag' ||
      actionId === 'BulkFolder' ||
      actionId === 'BulkAssign' ||
      actionId === 'QuickBatch' ||
      actionId === 'BulkRelease'
    ) {
      // prettier-ignore
      let actionType: 'TAG' | 'FOLDER' | 'ASSIGNMENT' | 'QUICKASSIGNMENT' | 'RELEASE' | '';

      if (actionId === 'BulkTag') {
        actionType = 'TAG';
      } else if (actionId === 'BulkFolder') {
        actionType = 'FOLDER';
      } else if (actionId === 'BulkAssign') {
        actionType = 'ASSIGNMENT';
      } else if (actionId === 'QuickBatch') {
        actionType = 'QUICKASSIGNMENT';
      } else if (actionId === 'BulkRelease') {
        actionType = 'RELEASE';
      } else {
        actionType = '';
      }

      const commonPayload = {
        sourceType: SourceTypeCode.DOCEXPLORER,
        actionType,
        elementID: 'divBulkAction',
        selectedHitsDetails: [],
        sourceDetails: JSON.stringify({
          SelectedNodeIDs: selectedNodeIds.join(','),
          Filters: prepareActiveFilterPayload(deepClone(activeFilters)),
          ColumnFilters: actionMenuColumnFilters,
          IsInverse: isAllSelected,
          SelectedDocIDs: isAllSelected ? '' : selectedIds.join(','),
          ExcludedDocIds: isAllSelected ? unselectedIds.join(',') : '',
        }),
      };

      const shouldUseNonBg =
        isNonbg && (actionType !== 'ASSIGNMENT' || 'QUICKASSIGNMENT');

      if (!shouldUseNonBg) {
        const callBulkActionsPopupPayload = {
          ...commonPayload,
          sourceTypeContext: SourceTypeCode.DOCEXPLORER,
          destinationTypeContext: '',
          documentCount: '',
          documentListMasterID: 0,
        };

        callBulkActionsPopup(callBulkActionsPopupPayload, {
          onSuccess: () => {
            switch (actionType) {
              case 'TAG':
                setIsBulkTagAction(true);
                break;
              case 'FOLDER':
                setIsBulkFolderAction(true);
                break;
              case 'ASSIGNMENT':
                setIsBulkAssignAction(true);
                break;
              case 'QUICKASSIGNMENT':
                setIsQuickBatchAction(true);
                break;
              case 'RELEASE':
                setIsBulkReleaseAction(true);
                break;
            }
          },
        });
      } else {
        callBulkActionsPopupNonBg(commonPayload, {
          onSuccess: () => {
            if (actionType === 'TAG') {
              setIsBulkTagAction(true);
            } else if (actionType === 'FOLDER') {
              setIsBulkFolderAction(true);
            }
          },
        });
      }

      return;
    }
  };
  const handleBulkFolderContinue = async (
    selectedAction: string,
    docCount?: number,
    newFolderId?: number,
    validIds?: string[]
  ) => {
    setBulkSelectedAction(selectedAction);
    setNewFolderId(newFolderId?.toString());

    const bulkActionTreeIDs = (() => {
      const ids = newFolderId ? [newFolderId.toString()] : validIds;

      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    if (selectedAction === 'folderDocs') {
      callBulkActionsLikePopupApiCall(
        'FOLDER',
        bulkActionTreeIDs,
        selectedAction,
        undefined,
        () => {
          setIsActionLikeModal('bulkFolder');
        },
        undefined,
        docCount
      );
    } else if (selectedAction === 'unfolderDocs') {
      setIsBulkFolderAction(false);
      doActionApiCall('FOLDER', bulkActionTreeIDs, selectedAction);
    }
  };
  const handleBulkTagContinue = async (
    selectedAction: string,
    docCount?: number,
    newTagId?: number,
    validIds?: string[]
  ) => {
    setBulkSelectedAction(selectedAction);
    setNewTagId(newTagId?.toString());
    const bulkActionTreeIDs = (() => {
      const ids = newTagId ? [newTagId.toString()] : validIds;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    if (selectedAction === 'untagDocs') {
      setIsBulkWarning(true);
    } else if (selectedAction === 'tagDocs') {
      callBulkActionsLikePopupApiCall(
        'TAG',
        bulkActionTreeIDs,
        selectedAction,
        undefined,
        () => {
          setIsActionLikeModal('bulkTag');
        }
      );
    }
  };

  const handleBulkRelease = (selectedIds: string[]) => {
    setBulkSelectedAction('bulkRelease');
    setSecurityGroupId(selectedIds);
    callBulkActionsLikePopupApiCall(
      'RELEASE',
      selectedIds,
      'bulkRelease',
      masterDocListId,
      () => {
        setIsActionLikeModal('bulkRelease');
      }
    );
  };

  const handleBulkUnrelease = (selectedIds: string[]) => {
    doActionApiCall('RELEASE', selectedIds, 'bulkUnrelease');
    setIsBulkReleaseAction(false);
  };

  const handleActionLike = (docCount?: number) => {
    if (isActionLikeModal === 'bulkTag') {
      setIsBulkWarning(true);
    }
    if (isActionLikeModal === 'bulkFolder') {
      setIsBulkFolderAction(false);
      const bulkActionTreeIDs = (() => {
        const ids = newFolderId
          ? [newFolderId]
          : selectedNewExistingFolder?.ids;

        if (Array.isArray(ids)) return ids;
        if (typeof ids === 'string') return [ids];
        return [];
      })();
      doActionApiCall(
        'FOLDER',
        bulkActionTreeIDs,
        bulkSelectedAction,
        undefined,
        docCount
      );
    }

    if (isActionLikeModal === 'bulkAssign') {
      setIsBulkAssignAction(false);
      doActionApiCall(
        'ASSIGN',
        selectedTreeIds,
        bulkSelectedAction,
        undefined,
        docCount
      );
    }

    if (isActionLikeModal === 'quickBatch') {
      pendingAssignAction.current = 'quickBatch';
      doActionApiCall(
        'ASSIGN',
        [''],
        bulkSelectedAction,
        quickBatchAssignmentId,
        docCount
      );
      setIsQuickBatchAction(false);
    }

    if (isActionLikeModal === 'bulkRelease') {
      setIsBulkReleaseAction(false);
      doActionApiCall('RELEASE', securityGroupId, bulkSelectedAction);
    }
    setIsActionLikeModal(false);
    setAnalyticsIndicatorStatus(0);
  };
  const handleBulkWarning = () => {
    setActionLikeChangeList(undefined);
    const bulkActionTreeIDs = (() => {
      const ids = newTagId ? [newTagId] : selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    doActionApiCall('TAG', bulkActionTreeIDs, bulkSelectedAction);
  };

  const handleBulkAssignContinue = async (payload: {
    action: 'assign_documents' | 'unassign_documents';
    isNewAssignment: boolean;
    samplingMethod?: string;
    persistContent?: boolean;
    count?: number;
    percent?: number;
    docCount?: number;
  }) => {
    const {
      action,
      isNewAssignment,
      samplingMethod = 'count',
      persistContent = false,
      count = 1,
      percent = 1,
      docCount = 0,
    } = payload;

    setBulkSelectedAction(action);
    const isAdd = action !== 'unassign_documents';
    const isNew = isAdd ? isNewAssignment : false;

    const mapSamplingMethodToApi = (method: string): string => {
      const mapping: Record<string, string> = {
        count: 'STANDARD',
        percent: 'PERCENTAGE',
        parent_level_docs_only: 'PARENTLEVEL',
        inclusive_email: 'INCLUSIVEEMAIL',
      };
      return mapping[method] || 'STANDARD';
    };

    const mappedSamplingMethod = mapSamplingMethodToApi(samplingMethod);

    const mapNumberToAssign =
      samplingMethod === 'count'
        ? count.toString()
        : samplingMethod === 'percent'
          ? percent !== undefined
            ? percent.toString()
            : ''
          : '0';

    samplingMethodForAssign.current = samplingMethod;

    countForAssign.current = count;
    percentForAssign.current = percent;

    const bulkAssignDocIdPayload: IBulkAssignDocListIdRequest = {
      ActionType: 'ASSIGNMENT',
      ElementID: 'divBulkAction',
      BulkActionContextTypeCode: SourceTypeCode.DOCEXPLORER,
      DocumentCount: '0',
      ContextContainer: {
        SelectedNodeIDs: selectedNodeIds.join(','),
        Filters: prepareActiveFilterPayload(deepClone(activeFilters)),
        ColumnFilters: actionMenuColumnFilters,
        IsInverse: isAllSelected,
        SelectedDocIds: isAllSelected ? '' : selectedIds.join(','),
        ExcludedDocIds: isAllSelected ? unselectedIds.join(',') : '',
      },
      UniqueDocumentCount: 0,
      DestinationContextTypeCode: 'BULKASSIGN',
      SampleMethod: mappedSamplingMethod,
      SourceContextTypeCode: 'ASSIGNDOCNOC',
      BulkActionTreeIDs: selectedTreeIds,
      IsAdd: isAdd,
      IsNewAssignment: isNew,
      IsPersistSearchHits: persistContent,
      BulkActionTypeCode: 'ASSIGN',
      SourceContextID: masterDocListId.toString(),
      NumberToAssign:
        action === 'assign_documents' ? mapNumberToAssign : docCount.toString(),
    };

    persistContentForAssign.current = persistContent;
    pendingAssignAction.current = action;
    if (isNew) assignmentSelectedTab.current = 'new';

    // Set total doc number to unassign only for unassign action
    if (action == 'unassign_documents') {
      docNumberToUnassign.current = docCount;
    } else {
      docNumberToUnassign.current = 0;
    }
    getBulkAssignDocListId(bulkAssignDocIdPayload);
  };

  const handleQuickBatchContinue = (assignmentId?: number) => {
    setBulkSelectedAction('quickBatch');
    setQuickBatchAssignmentId(assignmentId);

    const createBulkTreeIds = assignmentId ? assignmentId.toString() : '0';
    const quickBatchDocIdPayload: IBulkAssignDocListIdRequest = {
      ActionType: 'QUICKASSIGNMENT',
      ElementID: 'divBulkAction',
      BulkActionContextTypeCode: SourceTypeCode.DOCEXPLORER,
      DocumentCount: '0',
      ContextContainer: {
        SelectedNodeIDs: selectedNodeIds.join(','),
        Filters: prepareActiveFilterPayload(deepClone(activeFilters)),
        ColumnFilters: actionMenuColumnFilters,
        IsInverse: isAllSelected,
        SelectedDocIds: isAllSelected ? '' : selectedIds.join(','),
        ExcludedDocIds: isAllSelected ? unselectedIds.join(',') : '',
      },
      UniqueDocumentCount: 0,
      DestinationContextTypeCode: 'BULKASSIGN',
      SampleMethod: 'STANDARD',
      SourceContextTypeCode: 'ASSIGNDOCNOC',
      BulkActionTreeIDs: [createBulkTreeIds],
      IsAdd: true,
      IsNewAssignment: false,
      IsPersistSearchHits: true,
      IsQuick: true,
      BulkActionTypeCode: 'ASSIGN',
      SourceContextID: masterDocListId.toString(),
      NumberToAssign: '0',
    };
    pendingAssignAction.current = 'quickBatch';
    getBulkAssignDocListId(quickBatchDocIdPayload);
    setIsQuickBatchAction(true);
  };

  const getIncludeFlags = (
    actionLikeChangeList: IActionListItem[] | undefined
  ): {
    IncludeNearDuplicates: boolean;
    IncludeFamilyMember: boolean;
    IncludeEmailThreads: boolean;
  } => {
    if (!actionLikeChangeList) {
      return {
        IncludeNearDuplicates: false,
        IncludeFamilyMember: false,
        IncludeEmailThreads: false,
      };
    }

    const nearDuplicates = actionLikeChangeList.find(
      (item) => item.id === 'near_duplicates'
    );
    const familyMembers = actionLikeChangeList.find(
      (item) => item.id === 'family_members'
    );
    const emailDocs = actionLikeChangeList.find(
      (item) => item.id === 'email_docs'
    );

    return {
      IncludeNearDuplicates: nearDuplicates?.selected === true,
      IncludeFamilyMember: familyMembers?.selected === true,
      IncludeEmailThreads: emailDocs?.selected === true,
    };
  };

  const calculateFromPercentage = (
    total: number,
    percentage: number
  ): number => {
    const result = total * (percentage / 100);

    if (result < 1) {
      return -1;
    }

    const decimal = result - Math.floor(result);

    if (decimal < 0.5) {
      return Math.floor(result);
    } else {
      return Math.ceil(result);
    }
  };

  const doActionApiCall = (
    getActionType: string,
    bulkActionTreeIDs: string[],
    selectedAction: string,
    assignmentId?: number,
    docCount?: number,
    newMasterDocListId?: number
  ) => {
    const createAssignmentTreeId = assignmentId ? assignmentId : 0;
    const IsAdd =
      selectedAction === 'tagDocs' ||
      selectedAction === 'folderDocs' ||
      selectedAction === 'assign_documents' ||
      selectedAction === 'quickBatch' ||
      selectedAction === 'bulkRelease';

    let sampleMethod = 'STANDARD';
    let numberToAssignValue = '0';

    if (selectedAction === 'assign_documents') {
      const samplingMethod = samplingMethodForAssign.current;

      switch (samplingMethod) {
        case 'count': {
          sampleMethod = 'STANDARD';
          numberToAssignValue = countForAssign.current?.toString() ?? '0';
          break;
        }

        case 'percent': {
          sampleMethod = 'PERCENTAGE';
          const calculatedCount = calculateFromPercentage(
            docCount ?? 0,
            percentForAssign.current ?? 0
          );
          numberToAssignValue = calculatedCount.toString();
          break;
        }

        case 'parent_level_docs_only': {
          sampleMethod = 'PARENTLEVEL';
          numberToAssignValue = '0';
          break;
        }

        case 'inclusive_email': {
          sampleMethod = 'INCLUSIVEEMAIL';
          numberToAssignValue = '0';
          break;
        }

        default: {
          sampleMethod = 'STANDARD';
          numberToAssignValue = '0';
          break;
        }
      }
    } else {
      sampleMethod = 'STANDARD';
      numberToAssignValue = docCount !== undefined ? docCount.toString() : '0';
    }

    const includeFlags = getIncludeFlags(actionLikeChangeList);

    const doActionPayload = {
      sourceType: SourceTypeCode.DOCEXPLORER,
      actionType: getActionType,
      elementId: 'divBulkAction',
      selectedList: {
        SelectedNodeIDs: selectedNodeIds.join(','),
        Filters: prepareActiveFilterPayload(deepClone(activeFilters)),
        ColumnFilters: actionMenuColumnFilters,
        IsInverse: isAllSelected,
        SelectedDocIds: isAllSelected ? '' : selectedIds.join(','),
        ExcludedDocIds: isAllSelected ? unselectedIds.join(',') : '',
      },
      destinationList: {
        BulkActionTreeIDs:
          selectedAction === 'quickBatch'
            ? [createAssignmentTreeId]
            : bulkActionTreeIDs,
        IsAdd: IsAdd,
        SampleMethod: sampleMethod,
        NumberToAssign: numberToAssignValue,
        MasterDocListID:
          newMasterDocListId?.toString() ?? masterDocListId.toString(),
        ...(selectedAction === 'quickBatch' && {IsQuick: true}),
        ...(IsAdd === true && {
          IncludeNearDuplicates: includeFlags.IncludeNearDuplicates,
          IncludeFamilyMember: includeFlags.IncludeFamilyMember,
          IncludeEmailThreads: includeFlags.IncludeEmailThreads,
        }),
        ...(selectedAction === 'quickBatch' && {
          actionType: 'BulkAssign',
          IsPersistSearchHits: true,
          DestinationContextTypeCode: 'BULKASSIGN',
          CreateAssignment: false,
          AssignmentId: 0,
          IsNewAssignment: false,
        }),
        ...(selectedAction === 'assign_documents' && {
          actionType: 'BulkAssign',
          IsPersistSearchHits: persistContentForAssign.current,
          DestinationContextTypeCode: 'BULKASSIGN',
          CreateAssignment: assignmentSelectedTab.current === 'new',
          AssignmentId:
            assignmentSelectedTab.current === 'new' ? bulkAssignmentId : 0,
          IsNewAssignment: assignmentSelectedTab.current === 'new',
        }),
      },
      masterDocListID: newMasterDocListId ?? masterDocListId,
      strSelections: null,
      ...(selectedAction === 'quickBatch' && {IsQuick: true}),
      ...(selectedAction === 'quickBatch' && {IsAdd: IsAdd}),
    };

    const doActionNonBgPayload = {
      sourceType: SourceTypeCode.DOCEXPLORER,
      actionType: getActionType,
      elementId: 'divBulkAction',
      SelectedList: {
        LstBulkActionContext: selectedIds.map((id) => ({ID: id})),
      },
      destinationList: {
        BulkActionTreeIDs: bulkActionTreeIDs,
        IsAdd: false,
        SampleMethod: '',
        NumberToAssign: '0',
      },
      strSelections: null,
    };
    if (!isNonbg) {
      doAction(doActionPayload);
    } else {
      doActionNonBg(doActionNonBgPayload);
    }
  };

  const callBulkActionsLikePopupApiCall = (
    getActionType: string,
    bulkActionTreeIDs: string[],
    selectedAction?: string,
    masterDocListIdToUse?: number,
    onSuccessCallback?: () => void,
    persistContent?: boolean,
    docCount?: number
  ) => {
    const bulkTreeIDs = bulkActionTreeIDs.map(Number);
    const masterDocListIdForPayload = masterDocListIdToUse ?? masterDocListId;
    const likePopupPayload: IBulkActionLikePopupRequest = {
      sourceType: SourceTypeCode.DOCEXPLORER,
      actionType: getActionType,
      elementID: 'divBulkAction',
      sourceTypeContext: '',
      sourceDetails: JSON.stringify({
        SelectedNodeIDs: selectedNodeIds.join(','),
        Filters: prepareActiveFilterPayload(deepClone(activeFilters)),
        ColumnFilters: actionMenuColumnFilters,
        IsInverse: isAllSelected,
        SelectedDocIds: isAllSelected ? '' : selectedIds.join(','),
        ExcludedDocIds: isAllSelected ? unselectedIds.join(',') : '',
      }),
      destinationTypeContext: '',
      selectedHitsDetails: [],
      documentCount: '0',
      masterDocListID: masterDocListIdForPayload,
      isAdd: true,
      documentListMasterID: 0,
      docAssign: docCount ? docCount : 0,
      selectedDetinationIds: JSON.stringify({
        BulkActionTreeIDs: bulkTreeIDs,
      }),
    };

    if (getActionType === 'QUICKASSIGNMENT') {
      likePopupPayload.isPersistSearchHits = true;
    } else if (getActionType === 'ASSIGN') {
      likePopupPayload.isPersistSearchHits =
        persistContent ?? persistContentForAssign.current;
    }

    if (!isNonbg) {
      callBulkActionsLikePopup(likePopupPayload, {
        onSuccess: () => {
          if (onSuccessCallback) {
            onSuccessCallback();
          }
        },
      });
    } else {
      callBulkActionsLikePopupNonbg(likePopupPayload);

      if (onSuccessCallback) {
        onSuccessCallback();
      }
    }
  };

  const onActionLikeChange = (
    index: number,
    status: boolean,
    updatedActionItems: IActionListItem[]
  ) => {
    setActionLikeChangeList(updatedActionItems);
  };

  const getSelectedNewExistingTag = (): string[] => {
    const ids = selectedNewExistingTag?.ids;
    if (!isEmpty(ids)) {
      if (typeof ids === 'string') {
        return ids ? [ids] : [];
      }
      return ids || [];
    }
    return [];
  };

  return (
    <>
      <Box className="view-doc-explorer">
        <Box className="view-doc-header">
          <DocExplorerHeader />
        </Box>
        <Divider className="view-doc-divider" />
        <Box className="view-doc-explorer-content">
          <Box className="tree-view-section">
            <ExpandableTreeView
              itemsList={docsData}
              expandedNodes={['-1']}
              onNodeExpand={handleNodeExpand}
              multipleSelection={false}
              isShowCountLabel={false}
              isShowCheckbox={false}
              setSelectedItem={handleSelectedNode}
              selectedItem={selectedNodeIds[0]}
              initialWidth={320}
              minWidth={72}
              maxWidth={window.innerWidth - 120}
            />
          </Box>

          <Box className="view-doc-explorer-filter-table-section">
            <Box className="view-doc-explorer-filter">
              <DocExplorerFilterAccordion
                key={dataGridResetKey}
                filters={metadataFilters ?? []}
                title={t('docExplorer.filterAccordion.title')}
                expand={true}
                expandText={t('docExplorer.filterAccordion.expandText')}
                collapseText={t('docExplorer.filterAccordion.collapseText')}
                tooltipText={t('docExplorer.filterAccordion.tooltipText')}
                tooltipTitle={t('docExplorer.filterAccordion.tooltipTitle')}
                onFilterApply={setActiveFilters}
              />
            </Box>
            <Box>
              <TableHeader
                showColumnsIcon={true}
                showResetIcon
                showSaveIcon
                showActionButton
                tableColumns={columnMenu}
                onResetClick={columnResetClickHandler}
                onSaveClick={columnSaveClickHandler}
                onTableColumnSelectionChange={columnMenuSelectionChangeHandler}
                hasSelectedRows={enableActionButton}
                actions={filteredActions}
                onActionSelect={onActionSelect}
              />
            </Box>

            <Box className="doc-datagrid-container">
              <DocExplorerDataGrid
                key={dataGridResetKey}
                columnConfig={docColumnConfig}
                onColumnOrderChange={columnOrderChangeHandler}
                onSelectionStateChange={handleSelectionStateChange}
                selectedIds={selectedIds}
                unselectedIds={unselectedIds}
                isAllSelected={isAllSelected}
                selectedNodeID={selectedNodeIds.join(',')}
                activeFilters={activeFilters}
                onColumnFiltersChange={setColumnFilters}
                columnResize={true}
                autoColumnSize={true}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
      {isBulkFolderAction && (
        <BulkFolderUnFolder
          data-testid="bulk-folder"
          isOpen={isBulkFolderAction}
          setOpenActionMenu={setIsBulkFolderAction}
          handleBulkFolderContinue={handleBulkFolderContinue}
          masterDocListId={masterDocListId}
          setSelectedNewExistingFolder={setSelectedNewExistingFolder}
          isNonbg={false}
        />
      )}

      {isBulkActionsPopupLoading && (
        <ArrLoadingSpinner overlay isOpen={isBulkActionsPopupLoading} />
      )}
      {isBulkTagAction && masterDocListId > 0 && (
        <BulkTagUntag
          isOpen={isBulkTagAction}
          setOpenActionMenu={setIsBulkTagAction}
          handleBulkTagContinue={handleBulkTagContinue}
          masterDocListId={masterDocListId}
          setSelectedNewExistingTag={setSelectedNewExistingTag}
          isNonbg={false}
        />
      )}
      {isBulkReleaseAction && (
        <BulkReleaseFlyout
          isOpen={isBulkReleaseAction}
          onClose={() => {
            setIsBulkReleaseAction(false);
          }}
          onRelease={handleBulkRelease}
          onUnrelease={handleBulkUnrelease}
        />
      )}
      {isBulkAssignAction && (
        <BulkAssignFlyout
          isOpen={isBulkAssignAction}
          onClose={() => {
            setIsBulkAssignAction(false);
            samplingMethodForAssign.current = 'count';
            countForAssign.current = 1;
            percentForAssign.current = 1;
            persistContentForAssign.current = false;
          }}
          onContinue={handleBulkAssignContinue}
          selectedIds={selectedIds}
          masterDocListId={masterDocListId}
          onTreeSelect={(ids) => setSelectedTreeIds(ids ?? [])}
        />
      )}
      {isQuickBatchAction && (
        <QuickBatchFlyout
          onClose={() => setIsQuickBatchAction(false)}
          onContinue={handleQuickBatchContinue}
          selectedIds={selectedIds}
          isOpen={isQuickBatchAction}
          masterDocListId={masterDocListId}
        />
      )}
      {isActionLikeModal && (
        <ActionLikeDocuments
          data-testid="action-like"
          selectedIds={selectedIds}
          showModal={!!isActionLikeModal}
          onClose={() => {
            setIsActionLikeModal(false);
            setActionLikeChangeList(undefined);
            setAnalyticsIndicatorStatus(0);
            if (
              selectedNewExistingTag?.type === 'new' ||
              selectedNewExistingFolder?.type === 'new'
            ) {
              setIsBulkTagAction(false);
              setIsBulkFolderAction(false);
            }
            if (isActionLikeModal === 'bulkAssign') {
              if (assignmentSelectedTab.current === 'new') {
                setIsBulkAssignAction(false);
                assignmentSelectedTab.current = 'existing';
                setSelectedTreeIds([]);
                setBulkAssignmentId(0);
              }
              samplingMethodForAssign.current = 'count';
              countForAssign.current = 1;
              percentForAssign.current = 1;
              persistContentForAssign.current = false;
            }
          }}
          actionList={actionList}
          onCancel={() => {
            setIsActionLikeModal(false);
            setActionLikeChangeList(undefined);
            setAnalyticsIndicatorStatus(0);
            if (
              selectedNewExistingTag?.type === 'new' ||
              selectedNewExistingFolder?.type === 'new'
            ) {
              setIsBulkTagAction(false);
              setIsBulkFolderAction(false);
            }
            if (isActionLikeModal === 'bulkAssign') {
              if (assignmentSelectedTab.current === 'new') {
                setIsBulkAssignAction(false);
                assignmentSelectedTab.current = 'existing';
                setSelectedTreeIds([]);
                setBulkAssignmentId(0);
              }
              samplingMethodForAssign.current = 'count';
              countForAssign.current = 1;
              percentForAssign.current = 1;
              persistContentForAssign.current = false;
            }
          }}
          onContinue={handleActionLike}
          continueButtonLabel={t(
            `docExplorer.actionMenu.actionLikeDocuments.finalize.${isActionLikeModal}`
          )}
          onActionChange={onActionLikeChange}
          masterDocListId={masterDocListId}
          analyticsIndicatorStatus={analyticsIndicatorStatus}
          actionType={
            isActionLikeModal === 'bulkAssign'
              ? 'ASSIGNMENT'
              : isActionLikeModal === 'bulkTag'
                ? 'TAG'
                : isActionLikeModal === 'bulkFolder'
                  ? 'FOLDER'
                  : isActionLikeModal === 'quickBatch'
                    ? 'QUICKASSIGNMENT'
                    : isActionLikeModal === 'bulkRelease'
                      ? 'RELEASE'
                      : undefined
          }
        />
      )}
      {isBulkWarning && (
        <BulkTaggingWarning
          onClose={() => {
            setIsBulkWarning(false);
            setActionLikeChangeList(undefined);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
          }}
          showModal={isBulkWarning}
          sourceType={SourceTypeCode.DOCEXPLORER}
          actionLikeChangeList={actionLikeChangeList}
          masterDocListId={Number(masterDocListId)}
          selectedNewExistingTag={getSelectedNewExistingTag()}
          onFooterClick={() => {
            navigate('/Report/Tally');
          }}
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setIsBulkWarning(false);
                setActionLikeChangeList(undefined);
                if (selectedNewExistingTag?.type === 'new') {
                  setIsBulkTagAction(false);
                }
              },
              variant: 'secondary',
            },
            {
              id: '2',
              label: t('common.ok'),
              onClick: handleBulkWarning,
              variant: 'primary',
            },
          ]}
        />
      )}
      {isLoading && <ArrLoadingSpinner overlay isOpen={isLoading} />}

      <ArrDocMaxLimitWarning
        totalDocumentCount={
          isAllSelected
            ? totalGridCount - unselectedIds.length
            : selectedIds.length
        }
        show={showDocLimitWarning}
        destination={navigationType}
        onCancel={() => setShowDocLimitWarning(false)}
        onContinue={(totalDocumentToView: number) =>
          startDocumentNavigation(selectedActionId, totalDocumentToView)
        }
      />
    </>
  );
};
